/**
 * SharedNoteContent — read-only Tiptap renderer for a shared note's markdown.
 *
 * Shared by the public share page (`pages/SharePage.tsx`) and the in-app
 * shared-with-me viewer (`views/SharedNoteViewer.tsx`). Note mentions render
 * as links when the mentioned note is itself shared (present in mentionMap),
 * and as inert text otherwise.
 */

import LinkExt from "@tiptap/extension-link";
import Mention from "@tiptap/extension-mention";
import { Table } from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import type { NodeViewProps } from "@tiptap/react";
import {
  EditorContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  useEditor,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { createElement, useEffect, useMemo, useRef } from "react";
import { Markdown } from "tiptap-markdown";
import { CodeBlockExtension } from "../editor/CodeBlockExtension";
import { HashtagExtension } from "../editor/HashtagExtension";
import { ImageExtension } from "../editor/ImageExtension";
import "../pages/share.css";
import "./editor/editor.css";
import { BlockMathExtension, InlineMathExtension } from "../editor/MathExtension";

export type MentionMap = Record<string, { title: string }>;

function ShareMentionView({
  node,
  mentionMapRef,
}: NodeViewProps & { mentionMapRef: React.RefObject<MentionMap> }) {
  const { id, label } = node.attrs as { id: string; label: string };
  const entry = mentionMapRef.current?.[id];

  if (entry) {
    return createElement(
      NodeViewWrapper,
      { as: "span" },
      createElement(
        "a",
        {
          className: "caliu-mention--shared",
          href: `/share/${id}`,
        },
        label,
      ),
    );
  }

  return createElement(
    NodeViewWrapper,
    { as: "span" },
    createElement("span", { className: "caliu-mention--unshared" }, label),
  );
}

export function parseContent(raw: string) {
  if (raw.trimStart().startsWith("{")) {
    try {
      return JSON.parse(raw);
    } catch {
      /* fall through */
    }
  }
  return raw;
}

export function SharedNoteContent({
  content,
  mentionMap,
}: {
  content: string;
  mentionMap: MentionMap;
}) {
  const parsed = parseContent(content);
  const mentionMapRef = useRef<MentionMap>(mentionMap);
  mentionMapRef.current = mentionMap;

  const mentionExt = useMemo(() => {
    const ref = mentionMapRef;
    return Mention.extend({
      name: "noteMention",
      addStorage() {
        return {
          ...this.parent?.(),
          markdown: {
            serialize(
              state: { write: (s: string) => void },
              node: { attrs: { label?: string; id?: string } },
            ) {
              const id = node.attrs.id ?? "";
              const label = node.attrs.label ?? id;
              state.write(`[[${id}|${label}]]`);
            },
            parse: {
              updateDOM(element: HTMLElement) {
                const WIKILINK_RE = /\[\[([^\]|]+)\|([^\]]+)\]\]/g;
                const walker = document.createTreeWalker(
                  element,
                  NodeFilter.SHOW_TEXT,
                );
                const textNodes: Text[] = [];
                while (walker.nextNode()) {
                  const node = walker.currentNode as Text;
                  if (node.textContent && WIKILINK_RE.test(node.textContent)) {
                    textNodes.push(node);
                  }
                  WIKILINK_RE.lastIndex = 0;
                }
                for (const textNode of textNodes) {
                  const text = textNode.textContent ?? "";
                  const fragment = document.createDocumentFragment();
                  let lastIndex = 0;
                  WIKILINK_RE.lastIndex = 0;
                  let match: RegExpExecArray | null;
                  while ((match = WIKILINK_RE.exec(text)) !== null) {
                    if (match.index > lastIndex) {
                      fragment.appendChild(
                        document.createTextNode(
                          text.slice(lastIndex, match.index),
                        ),
                      );
                    }
                    const span = document.createElement("span");
                    span.setAttribute("data-type", "noteMention");
                    span.setAttribute("data-id", match[1]);
                    span.setAttribute("data-label", match[2]);
                    span.classList.add("caliu-mention");
                    span.textContent = match[2];
                    fragment.appendChild(span);
                    lastIndex = WIKILINK_RE.lastIndex;
                  }
                  if (lastIndex < text.length) {
                    fragment.appendChild(
                      document.createTextNode(text.slice(lastIndex)),
                    );
                  }
                  textNode.parentNode?.replaceChild(fragment, textNode);
                }
              },
            },
          },
        };
      },
      addNodeView() {
        return ReactNodeViewRenderer((props: NodeViewProps) =>
          createElement(ShareMentionView, { ...props, mentionMapRef: ref }),
        );
      },
    }).configure({
      HTMLAttributes: { class: "caliu-mention" },
      suggestion: { items: () => [], render: () => ({}) },
    });
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      Markdown.configure({ html: false }),
      TaskList,
      TaskItem.configure({ nested: true }),
      ImageExtension,
      LinkExt.configure({
        openOnClick: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      CodeBlockExtension,
      HashtagExtension,
      InlineMathExtension,
      BlockMathExtension,
      mentionExt,
    ],
    content: parsed,
    editable: false,
  });

  useEffect(() => {
    if (editor && content) {
      editor.commands.setContent(parseContent(content));
    }
  }, [editor, content]);

  return (
    <div className="share-content">
      <div className="tiptap-editor">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
